import {
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Popover,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import {
  DeleteForever,
  ExpandLess,
  ExpandMore,
  PhotoCameraOutlined,
  UsbRounded
} from '@mui/icons-material';
import React, { useState, useMemo, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Camera } from '../../model/camera';
import DataItem from '../commons/DataItem';
import FailedImage from './FailedImages/FailedImages';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ConfirmationModal from '../commons/ConfirmationModal';
import { fetchCameraIdentifier } from '../../services/CameraService';
import { CameraStatus } from '../../model/camera-status';
import { CameraStatusDetailsType } from '../../model/camera-status-details-type';

interface IProps {
  camera: Camera;
  date: string;
  userCanDelete: boolean;
  groupPicturesExpected: boolean;
  onDeleteCamera: (camera: Camera) => void;
}

function CameraCard({ camera, date, userCanDelete, onDeleteCamera, groupPicturesExpected }: IProps) {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 900px)' });
  const [expanded, setExpanded] = useState(!isTabletOrMobile);
  const message = 'These images are missing : ';
  const [openDeleteCameraConfirmationModalFlag, setOpenDeleteCameraConfirmationModalFlag] = useState(false);
  const [cameraId, setCameraId] = useState('');

  useEffect(() => {
    const _cameraId = fetchCameraIdentifier(camera.cameraId, groupPicturesExpected);
    setCameraId(_cameraId);
  }, [camera?.cameraId, groupPicturesExpected]);

  const getStatus = (status?: CameraStatus) => {
    switch (status) {
      case 'FAILED':
        return { title: 'FAILED TO UPLOAD', color: '#FCE2E3' };

      case 'COMPLETE':
        return { title: 'COMPLETE', color: '#E3F6CE' };

      case 'ERROR':
        return { title: 'ERROR', color: '#FFA500' };

      default:
        return { title: 'IN PROGRESS', color: '#FFEECA' };
    }
  };

  const getUploadStatus = (subjectsCount: number, groupsCount: number) => {
    let uploadStatus = groupsCount ? `${groupsCount} groups` : '';

    if (subjectsCount) {
      return uploadStatus ? `${subjectsCount} subjects, ${uploadStatus}` : `${subjectsCount} subjects`;
    }

    return uploadStatus;
  };

  const memoizedUploadStatus = useMemo(
    () => getUploadStatus(camera.subjectsCount, camera.groupsCount),
    [camera.subjectsCount, camera.groupsCount]
  );

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleInformationClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleInformationClose = () => {
    setAnchorEl(null);
  };

  const deleteCamera = () => {
    setOpenDeleteCameraConfirmationModalFlag(false);
    onDeleteCamera(camera);
  };

  return (
    <>
      <Card sx={{ mt: '30px', mb: '30px', position: 'relative' }}>
        {camera.status !== 'COMPLETE' && userCanDelete && (
          <IconButton
            color="error"
            sx={{ position: 'absolute', top: '-10px', right: '-10px' }}
            onClick={() => setOpenDeleteCameraConfirmationModalFlag(true)}
          >
            <DeleteForever />
          </IconButton>
        )}

        <CardContent sx={{ backgroundColor: 'background.default', p: 3 }}>
          <Grid container>
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
              {camera.mediaId === 'DVD' ? (
                <UsbRounded color="primary" sx={{ fontSize: 32, transform: 'rotate(90deg)' }} />
              ) : (
                <PhotoCameraOutlined color="primary" sx={{ fontSize: 32 }} />
              )}
            </Grid>
            <Grid item xs={10}>
              <Typography variant="h4" color="primary">
                CAMERA {cameraId}
              </Typography>
              <Typography color="primary.dark" sx={{ fontWeight: 600 }}>
                {camera.photographerId}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent sx={{ pb: 0 }}>
          <DataItem title="UPLOAD STATUS:" data={memoizedUploadStatus} />

          <Paper
            sx={{
              textAlign: 'center',
              fontWeight: '500',
              padding: '10px',
              borderRadius: '21px',
              boxShadow: 'none',
              backgroundColor: getStatus(camera.status).color,
            }}
            data-testid="status-paper"
          >
            {(getStatus(camera.status).title === 'ERROR' ||
              (camera.status === 'PENDING' &&
                camera.statusDetails?.statusDetailsType ===
                CameraStatusDetailsType.WEB_UPLOAD_NOT_COMPLETE)) ? (
              <>
                {getStatus(camera.status).title}

                <IconButton sx={{ py: 0 }} onClick={handleInformationClick}>
                  <InfoIcon
                    fontSize="small"
                    color={camera.status === 'ERROR' ? 'error' : 'warning'}
                  />
                </IconButton>
              </>
            ) : (
              <>{getStatus(camera.status).title}</>
            )}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleInformationClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <Box
                sx={{
                  p: 1,
                  backgroundColor:
                    camera.status === 'ERROR' ? 'error.main' : 'warning.main',
                  color:
                    camera.status === 'ERROR'
                      ? 'error.contrastText'
                      : 'warning.contrastText',
                }}
              >
                {camera.status === 'ERROR' ? (
                  !!camera?.missingCaptures && camera?.missingCaptures?.length > 1 ? (
                    `These images are missing: ${camera.missingCaptures
                      .map((name) => `${name}.jpg`)
                      .join(', ')}`
                  ) : camera?.missingCaptures?.length === 1 ? (
                    `Image ${camera.missingCaptures[0]}.jpg is missing`
                  ) : (
                    message
                  )
                ) : camera.status === 'PENDING' &&
                camera.statusDetails?.statusDetailsType ===
                CameraStatusDetailsType.WEB_UPLOAD_NOT_COMPLETE ? (
                  camera.statusDetails?.statusDetails
                ) : null}
              </Box>
            </Popover>
          </Paper>

          <Divider sx={{ mt: 1 }} />
          <Collapse in={expanded} sx={{ pt: 1 }}>
            <DataItem title="DATE:" data={date} />
            <DataItem title="COMPUTER #:" data={camera.computerNumber} />
            <DataItem title="IMAGES UPLOADED:" data={camera.assetCount?.toString()} />
            <DataItem title="IMAGES DELIVERED:" data={camera.completed?.toString()} />

            {!!camera.failed && camera.failed > 0 && (
              <FailedImage cameraId={camera.cameraId} failedCount={camera.failed?.toString()} date={date} />
            )}

            <Divider sx={{ mt: 1 }} />
          </Collapse>
        </CardContent>
        <Box sx={{ textAlign: 'end', m: 1.5 }} onClick={() => setExpanded(!expanded)}>
          {expanded ? (
            <Button sx={{ textTransform: 'unset', fontSize: '12px' }} endIcon={<ExpandLess />}>
              Hide Camera Details
            </Button>
          ) : (
            <Button sx={{ textTransform: 'unset', fontSize: '12px' }} endIcon={<ExpandMore />}>
              Show Camera Details
            </Button>
          )}
        </Box>
      </Card>
      <ConfirmationModal
        open={openDeleteCameraConfirmationModalFlag}
        title={'Delete Camera: ' + camera.cameraId + ' Media ID: ' + camera.mediaId}
        handleNo={() => setOpenDeleteCameraConfirmationModalFlag(false)}
        handleYes={() => deleteCamera()}
      >
        This will delete {memoizedUploadStatus ? memoizedUploadStatus + ' and ' : ''}
        {camera.assetCount?.toString()} images, are you sure you want to remove this camera?
      </ConfirmationModal>
    </>
  );
}

export default CameraCard;
